import { useState, useEffect, useCallback, useRef } from 'react';
import { isValidWord } from '../services/dictionary';
import { useI18n } from '../i18n/I18nContext';

type HowToPlayModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type HintWord = {
  word: string;
  score: number;
  tip?: string;
};

type HintResult = {
  words: HintWord[];
  bestWord: string;
  maxScore: number;
  hint: string;
};

function HowToPlayModal({ isOpen, onClose }: HowToPlayModalProps) {
  const { t } = useI18n();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface rounded-card shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-subtle">
          <h2 className="text-2xl font-bold text-primary">
            {t('practiceHowToPlay')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-hover rounded-lg transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto max-h-[60vh]">
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-practice text-white rounded-full flex items-center justify-center font-bold">1</span>
              <div>
                <p className="text-secondary">{t('practiceRule1')}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-practice text-white rounded-full flex items-center justify-center font-bold">2</span>
              <div>
                <p className="text-secondary">{t('practiceRule2')}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-practice text-white rounded-full flex items-center justify-center font-bold">3</span>
              <div>
                <p className="text-secondary">{t('practiceRule3')}</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="flex-shrink-0 w-8 h-8 bg-practice text-white rounded-full flex items-center justify-center font-bold">4</span>
              <div>
                <p className="text-secondary">{t('practiceRule4')}</p>
              </div>
            </li>
          </ul>

          <div className="mt-6 p-4 bg-practice-subtle rounded-xl">
            <h3 className="font-semibold text-practice-text mb-2">{t('practiceKeyboardTitle')}</h3>
            <ul className="text-sm text-practice-text space-y-1">
              <li><kbd className="px-2 py-1 bg-surface rounded border border-practice/20 font-mono text-sm">A-Z</kbd> {t('practiceKeyboardType')}</li>
              <li><kbd className="px-2 py-1 bg-surface rounded border border-practice/20 font-mono text-sm">Enter</kbd> {t('practiceKeyboardSubmit')}</li>
              <li><kbd className="px-2 py-1 bg-surface rounded border border-practice/20 font-mono text-sm">Backspace</kbd> {t('practiceKeyboardRemove')}</li>
              <li><kbd className="px-2 py-1 bg-surface rounded border border-practice/20 font-mono text-sm">Escape</kbd> {t('practiceKeyboardClear')}</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-subtle">
          <button
            onClick={onClose}
            className="w-full bg-practice hover:bg-practice-hover text-white font-bold py-3 px-6 rounded-btn transition-colors"
          >
            {t('practiceGotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}

const VOWELS = 'AEIOU';
const CONSONANTS = 'BCDFGHJKLMNPQRSTVWXYZ';
const GAME_TIME = 5 * 60; // 5 minutes in seconds
const ROUND_TIME = 60; // 60 seconds per round
const MIN_WORD_LENGTH = 3;
const POINTS_PER_LETTER = 5;
const BONUS_THRESHOLD = 5;
const BONUS_PERCENT = 0.02;

function generateLetters(): string[] {
  const letters: string[] = [];
  // Generate 3-4 vowels
  const vowelCount = Math.floor(Math.random() * 2) + 3;
  for (let i = 0; i < vowelCount; i++) {
    letters.push(VOWELS[Math.floor(Math.random() * VOWELS.length)]);
  }
  // Fill rest with consonants (total 10 letters)
  while (letters.length < 10) {
    letters.push(CONSONANTS[Math.floor(Math.random() * CONSONANTS.length)]);
  }
  // Shuffle
  return letters.sort(() => Math.random() - 0.5);
}

function calculateScore(word: string): { base: number; bonus: number; total: number } {
  const base = word.length * POINTS_PER_LETTER;
  const bonus = word.length >= BONUS_THRESHOLD ? Math.floor(base * BONUS_PERCENT) : 0;
  return { base, bonus, total: base + bonus };
}

export default function PracticePage() {
  const { t } = useI18n();
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [letters, setLetters] = useState<string[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [roundScore, setRoundScore] = useState(0);
  const [gameTime, setGameTime] = useState(GAME_TIME);
  const [roundTime, setRoundTime] = useState(ROUND_TIME);
  const [wordsPlayed, setWordsPlayed] = useState<{ word: string; score: number }[]>([]);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [hintResult, setHintResult] = useState<HintResult | null>(null);
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const gameTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const roundTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedWord = selectedIndices.map(i => letters[i]).join('');

  const startGame = useCallback(() => {
    setGameStarted(true);
    setGameOver(false);
    setLetters(generateLetters());
    setSelectedIndices([]);
    setTotalScore(0);
    setRoundScore(0);
    setGameTime(GAME_TIME);
    setRoundTime(ROUND_TIME);
    setWordsPlayed([]);
    setFeedback(null);
  }, []);

  const startNewRound = useCallback(() => {
    setLetters(generateLetters());
    setSelectedIndices([]);
    setRoundTime(ROUND_TIME);
    setRoundScore(0);
    setFeedback(null);
    setHintResult(null);
    setShowHint(false);
  }, []);

  const getHint = useCallback(async () => {
    if (isLoadingHint || letters.length === 0) return;

    setIsLoadingHint(true);
    setShowHint(true);

    try {
      const response = await fetch('/api/practice-hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ letters }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to get hint' }));
        throw new Error(error.error || 'Failed to get hint');
      }

      const data = await response.json();
      setHintResult(data);
    } catch (err) {
      setFeedback({
        message: err instanceof Error ? err.message : 'Failed to get hint',
        type: 'error'
      });
      setShowHint(false);
    } finally {
      setIsLoadingHint(false);
    }
  }, [letters, isLoadingHint]);

  const endGame = useCallback(() => {
    setGameOver(true);
    setGameStarted(false);
    if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    if (roundTimerRef.current) clearInterval(roundTimerRef.current);
  }, []);

  // Game timer
  useEffect(() => {
    if (gameStarted && !gameOver) {
      gameTimerRef.current = setInterval(() => {
        setGameTime(prev => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (gameTimerRef.current) clearInterval(gameTimerRef.current);
    };
  }, [gameStarted, gameOver, endGame]);

  // Round timer
  useEffect(() => {
    if (gameStarted && !gameOver) {
      roundTimerRef.current = setInterval(() => {
        setRoundTime(prev => {
          if (prev <= 1) {
            startNewRound();
            return ROUND_TIME;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (roundTimerRef.current) clearInterval(roundTimerRef.current);
    };
  }, [gameStarted, gameOver, startNewRound]);

  const toggleLetter = (index: number) => {
    if (isChecking) return;

    const existingIndex = selectedIndices.indexOf(index);
    if (existingIndex >= 0) {
      // Remove this letter and all after it
      setSelectedIndices(prev => prev.slice(0, existingIndex));
    } else {
      // Add letter
      setSelectedIndices(prev => [...prev, index]);
    }
    setFeedback(null);
  };

  const clearSelection = () => {
    setSelectedIndices([]);
    setFeedback(null);
  };

  const submitWord = useCallback(async () => {
    if (selectedWord.length < MIN_WORD_LENGTH) {
      setFeedback({ message: t('practiceMinLetters', { count: MIN_WORD_LENGTH }), type: 'error' });
      return;
    }

    setIsChecking(true);
    try {
      const valid = await isValidWord(selectedWord);
      if (valid) {
        const score = calculateScore(selectedWord);
        setTotalScore(prev => prev + score.total);
        setRoundScore(prev => prev + score.total);
        setWordsPlayed(prev => [...prev, { word: selectedWord, score: score.total }]);
        setFeedback({
          message: `+${score.total} ${t('practicePoints')}${score.bonus > 0 ? ` (${t('practiceBonus')} +${score.bonus})` : ''}`,
          type: 'success'
        });
        setSelectedIndices([]);
      } else {
        setFeedback({ message: t('practiceInvalidWord'), type: 'error' });
      }
    } catch {
      setFeedback({ message: t('practiceCheckFailed'), type: 'error' });
    } finally {
      setIsChecking(false);
    }
  }, [selectedWord, t]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Keyboard support
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!gameStarted || gameOver || isChecking) return;

      const key = e.key.toUpperCase();

      if (key === 'ENTER') {
        submitWord();
        return;
      }

      if (key === 'BACKSPACE' || key === 'DELETE') {
        setSelectedIndices(prev => prev.slice(0, -1));
        return;
      }

      if (key === 'ESCAPE') {
        clearSelection();
        return;
      }

      // Find first unselected letter matching key
      const availableIndex = letters.findIndex((letter, index) =>
        letter === key && !selectedIndices.includes(index)
      );

      if (availableIndex >= 0) {
        setSelectedIndices(prev => [...prev, availableIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameStarted, gameOver, isChecking, letters, selectedIndices, submitWord]);

  if (!gameStarted && !gameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-practice-subtle via-surface to-page py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-5xl font-bold font-heading text-primary mb-6">
            {t('practiceTitle')}
          </h1>
          <p className="text-xl text-secondary mb-8">
            {t('practiceSubtitle')}
          </p>

          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-8 mb-8">
            <h2 className="text-2xl font-semibold text-primary mb-4">{t('practiceHowToPlay')}</h2>
            <ul className="text-left text-secondary space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-practice-text font-bold">1.</span>
                <span>{t('practiceRule1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-practice-text font-bold">2.</span>
                <span>{t('practiceRule2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-practice-text font-bold">3.</span>
                <span>{t('practiceRule3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-practice-text font-bold">4.</span>
                <span>{t('practiceRule4')}</span>
              </li>
            </ul>
          </div>

          <button
            onClick={startGame}
            className="bg-practice hover:bg-practice-hover text-white font-bold py-4 px-12 rounded-btn text-xl transition-all transform hover:scale-105 shadow-lg"
          >
            {t('practiceStartGame')}
          </button>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-practice-subtle via-surface to-page py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-5xl font-bold font-heading text-primary mb-4">
            {t('practiceGameOver')}
          </h1>

          <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-8 mb-8">
            <div className="text-6xl font-bold text-practice mb-2">{totalScore}</div>
            <div className="text-xl text-muted">{t('practiceFinalScore')}</div>

            <div className="mt-6 text-lg text-secondary">
              {t('practiceWordsPlayed', { count: wordsPlayed.length })}
            </div>

            {wordsPlayed.length > 0 && (
              <div className="mt-6 max-h-48 overflow-y-auto">
                <div className="flex flex-wrap gap-2 justify-center">
                  {wordsPlayed.map((w, i) => (
                    <span key={i} className="bg-practice-subtle text-practice-text px-3 py-1 rounded-lg text-sm font-medium">
                      {w.word} (+{w.score})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={startGame}
            className="bg-practice hover:bg-practice-hover text-white font-bold py-4 px-12 rounded-btn text-xl transition-all transform hover:scale-105 shadow-lg"
          >
            {t('practicePlayAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-practice-subtle via-surface to-page py-4 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header with timers */}
        <div className="flex justify-between items-center mb-6">
          <div className="bg-surface/80 backdrop-blur-theme rounded-xl border border-subtle px-6 py-3">
            <div className="text-sm text-muted">{t('practiceTotalScore')}</div>
            <div className="text-3xl font-bold text-practice">{totalScore}</div>
          </div>

          <div className="flex gap-4">
            <div className="bg-surface/80 backdrop-blur-theme rounded-xl border border-subtle px-4 py-3 text-center">
              <div className="text-sm text-muted">{t('practiceGameTime')}</div>
              <div className={`text-2xl font-bold ${gameTime < 60 ? 'text-error' : 'text-primary'}`}>
                {formatTime(gameTime)}
              </div>
            </div>
            <div className="bg-surface/80 backdrop-blur-theme rounded-xl border border-subtle px-4 py-3 text-center">
              <div className="text-sm text-muted">{t('practiceRoundTime')}</div>
              <div className={`text-2xl font-bold ${roundTime < 10 ? 'text-warning animate-pulse' : 'text-primary'}`}>
                {roundTime}s
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowHowToPlay(true)}
            className="p-2 bg-surface/80 backdrop-blur-theme rounded-xl border border-subtle text-muted hover:text-practice-text transition-colors"
            title={t('practiceHowToPlay')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>

        {/* How to Play Modal */}
        <HowToPlayModal isOpen={showHowToPlay} onClose={() => setShowHowToPlay(false)} />

        {/* Selected word display */}
        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-6 mb-6">
          <div className="text-sm text-muted mb-2">{t('practiceSelectedWord')}</div>
          <div className="min-h-16 flex items-center justify-center">
            {selectedWord ? (
              <div className="flex gap-2">
                {selectedWord.split('').map((letter, i) => (
                  <div
                    key={i}
                    className="w-12 h-12 bg-practice text-white rounded-lg flex items-center justify-center text-2xl font-bold shadow-lg"
                  >
                    {letter}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted text-lg">{t('practiceSelectLetters')}</div>
            )}
          </div>

          {feedback && (
            <div className={`mt-4 text-center font-semibold ${
              feedback.type === 'success' ? 'text-success' :
              feedback.type === 'error' ? 'text-error' : 'text-finder'
            }`}>
              {feedback.message}
            </div>
          )}
        </div>

        {/* Letter grid */}
        <div className="bg-surface/80 backdrop-blur-theme rounded-card border border-subtle p-6 mb-6">
          <div className="grid grid-cols-5 gap-3 max-w-md mx-auto">
            {letters.map((letter, index) => {
              const isSelected = selectedIndices.includes(index);
              const selectionOrder = selectedIndices.indexOf(index) + 1;

              return (
                <button
                  key={index}
                  onClick={() => toggleLetter(index)}
                  disabled={isChecking}
                  className={`relative w-full aspect-square rounded-xl text-3xl font-bold transition-all transform ${
                    isSelected
                      ? 'bg-practice text-white scale-95 shadow-inner'
                      : 'bg-gradient-to-br from-surface-raised to-surface-alt text-primary hover:scale-105 hover:shadow-lg shadow-md'
                  } disabled:opacity-50`}
                >
                  {letter}
                  {isSelected && (
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-surface text-practice rounded-full text-sm font-bold flex items-center justify-center shadow">
                      {selectionOrder}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={clearSelection}
            disabled={selectedIndices.length === 0 || isChecking}
            className="px-8 py-3 rounded-btn font-semibold border-2 border-input-border text-secondary hover:bg-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('practiceClear')}
          </button>
          <button
            onClick={submitWord}
            disabled={selectedWord.length < MIN_WORD_LENGTH || isChecking}
            className="px-12 py-3 rounded-btn font-bold bg-practice hover:bg-practice-hover text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 shadow-lg disabled:shadow-none disabled:transform-none"
          >
            {isChecking ? t('practiceChecking') : t('practiceSubmit')}
          </button>
          <button
            onClick={getHint}
            disabled={isLoadingHint}
            className="px-8 py-3 rounded-btn font-semibold border-2 border-magic/40 text-magic-text hover:bg-magic-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <span>💡</span>
            {isLoadingHint ? t('practiceHintLoading') : t('practiceGetHint')}
          </button>
          <button
            onClick={startNewRound}
            className="px-8 py-3 rounded-btn font-semibold border-2 border-warning/40 text-warning hover:bg-warning/10 transition-colors"
          >
            {t('practiceSkipRound')}
          </button>
        </div>

        {/* Hint Panel */}
        {showHint && (
          <div className="mt-6 bg-magic-subtle/80 backdrop-blur-theme rounded-card border border-magic/30 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-magic-text flex items-center gap-2">
                <span>💡</span> {t('practiceHintTitle')}
              </h3>
              <button
                onClick={() => setShowHint(false)}
                className="text-magic-text hover:text-magic-hover"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {isLoadingHint ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-magic border-t-transparent"></div>
                <span className="ml-3 text-magic-text">{t('practiceHintLoading')}</span>
              </div>
            ) : hintResult ? (
              <div className="space-y-4">
                {/* General hint */}
                <p className="text-magic-text italic">{hintResult.hint}</p>

                {/* Best words */}
                <div>
                  <h4 className="text-sm font-medium text-magic-text mb-2">
                    {t('practiceHintBestWords')}
                  </h4>
                  <div className="space-y-2">
                    {hintResult.words.map((word, index) => (
                      <div
                        key={word.word}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          index === 0
                            ? 'bg-magic-subtle border border-magic/30'
                            : 'bg-surface/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-lg text-primary">
                            {word.word}
                          </span>
                          {word.tip && (
                            <span className="text-sm text-muted">
                              - {word.tip}
                            </span>
                          )}
                        </div>
                        <span className={`font-bold px-3 py-1 rounded-lg ${
                          index === 0
                            ? 'bg-magic text-white'
                            : 'bg-magic-subtle text-magic-text'
                        }`}>
                          {word.score} pts
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Max score callout */}
                <div className="flex items-center justify-center gap-2 pt-2 text-sm text-magic-text">
                  <span>{t('practiceHintMaxScore')}:</span>
                  <span className="font-bold text-lg">{hintResult.maxScore}</span>
                  <span>({hintResult.bestWord})</span>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Round score */}
        {roundScore > 0 && (
          <div className="mt-6 text-center text-muted">
            {t('practiceRoundScore')}: <span className="font-bold text-practice">{roundScore}</span>
          </div>
        )}
      </div>
    </div>
  );
}
